import React, { useCallback, useState } from 'react';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import FigmaButtonRow from '../../../Elements/FigmaButtonRow';
import GeneralEditWizzardBase from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseFigma';
import ErrorMessage from '../../../Elements/ErrorMessage';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';
import OrderDirectionsEnum from '../../../Enums/OrderDirectionsEnum';
import ShownPanelsEnum from '../../../Enums/ShownPanelsEnum';
import fileHelper from '../../../Helpers/fileHelper';
import TemplateVariantItem from '../../../Domain/Template/TemplateVariantItem';
import TemplateService from '../../../Services/TemplateService';
import TemplateContentEditor from './TemplateContentEditor';
import TemplateVariantSearchParams from './TemplateVariantSearchParams';
import TemplateWindowDescription from './TemplateWindowDescription';

// Every language and version of one template name, embedded in the detail popup's "Variants" tab, so
// its button row uses the inner-menu class to re-anchor into the popup header. Rows are added and
// versioned here; nothing about one is editable, so the template's own edit is on the page behind.
const TemplateVariantAdmin = ({ templateName }: { templateName: string }) => {
  const [searchItem, setSearchItem] = useState<any>(() => {
    const params: any = new (TemplateVariantSearchParams as any)(true);
    params.Name = templateName;
    return params;
  });
  // The item being worked on plus the ShownPanel of the open window; null closes every window
  const [selected, setSelected] = useState<any>(null);

  const refresh = useCallback(
    (refreshFlag: boolean) => {
      setSelected(null);
      if (refreshFlag === true) {
        setSearchItem((previous: any) => ({ ...previous, Name: templateName }));
      }
    },
    [templateName]
  );

  // TabOfDataTable's clear-all builds a fresh instance, so the name is re-stamped on every search
  const startSearch = useCallback(
    (newSearchItem: any) => {
      setSearchItem({ ...newSearchItem, Name: templateName });
    },
    [templateName]
  );

  const downloadTemplateFile = (item: any) => {
    TemplateService.GetTemplateById(item.TemplateId)
      .then((response: any) => {
        if (!response || !response.binaryContent) {
          ErrorMessage.SetErrorMessage('The template has no stored content to download.');
          return;
        }
        fileHelper.saveBase64File(response.binaryContent, `${response.name}.${response.type.toLowerCase()}`);
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage(
          'Error! ErrorCode: TVA60. The template could not be downloaded. Please notify IT!'
        );
      });
  };

  TemplateVariantItem.uploadFunction = (item: any) => setSelected({ ...item, ShownPanel: ShownPanelsEnum.FileUpload });
  TemplateVariantItem.downloadFunction = downloadTemplateFile;
  TemplateVariantItem.editContentFunction = (item: any) =>
    setSelected({ ...item, ShownPanel: ShownPanelsEnum.EditContent });

  const renderOnePanel = (panelId: string, content: any) => (selected?.ShownPanel === panelId ? content : null);

  const renderPanels = () => {
    // One description covers every window, following the item's ShownPanel
    const templateWindow = (
      <GeneralEditWizzardBase
        crudType={CrudTypesEnum.Create}
        editedItem={selected}
        clickOnCancel={() => refresh(false)}
        clickOnSave={() => refresh(true)}
        windowDescription={TemplateWindowDescription}
        errorCode='TVA76'
      />
    );

    // Its own component, not a wizard field: the editor is a two-pane layout, and the wizard's field
    // descriptions cannot host one
    const contentWindow = (
      <TemplateContentEditor
        item={selected}
        clickOnCancel={() => refresh(false)}
        clickOnSave={() => refresh(true)}
      />
    );

    return (
      <div>
        {renderOnePanel(ShownPanelsEnum.AddNewItem, templateWindow)}
        {renderOnePanel(ShownPanelsEnum.FileUpload, templateWindow)}
        {renderOnePanel(ShownPanelsEnum.EditContent, contentWindow)}
      </div>
    );
  };

  const renderButtonsAndPanels = () => (
    <>
      <FigmaButtonRow
        className={'InnerMenuButtonFigmaList'}
        buttons={
          [
            {
              type: ButtonTypeEnum.NormalNormal,
              color: ButtonColorsEnum.Primary,
              iconPosition: IconPositionEnum.Right,
              // Same insert as creating a template; a seeded Name makes that field read-only
              onClick: () =>
                setSelected({
                  ...new (TemplateVariantItem as any)(),
                  Name: templateName,
                  ShownPanel: ShownPanelsEnum.AddNewItem,
                }),
              label: 'Add Language',
              tooltip: 'Add another language to this template',
              iconName: 'fa-solid fa-plus-circle',
            },
          ] as any
        }
      />
      {renderPanels()}
    </>
  );

  return (
    <div className='contentwrapper'>
      {renderButtonsAndPanels()}
      <TabOfDataTable
        type={TemplateVariantItem}
        serviceFunction={TemplateService.GetTemplateVariantsBySearching}
        convertDBItemsFunction={TemplateService.ConvertDBVariantItems}
        searchItem={searchItem}
        searchParamsType={TemplateVariantSearchParams}
        clickOnStartSearch={startSearch}
        shownPanel={selected?.ShownPanel ?? ShownPanelsEnum.None}
        showSearchStrip={true}
        popupEnabled={false}
        orderBy='Locale'
        orderDirection={OrderDirectionsEnum.Asc}
        errorCode='TVA125'
      />
    </div>
  );
};

export default TemplateVariantAdmin;
